import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { mapGraphEventToOutlookMeeting } from '../outlook-import/map-graph-event';
import { GraphCalendarViewResponse, OutlookMeeting } from '../outlook-import/outlook-import.types';

const CALENDAR_VIEW_ENDPOINT = 'https://graph.microsoft.com/v1.0/me/calendarView';
const MS_SCOPES = ['Calendars.Read', 'User.Read'];
const PAGE_SIZE = 100;
// Guard against pathological ranges producing endless paging.
const MAX_PAGES = 20;

const RETRYABLE_STATUSES = new Set([429, 503]);
const MAX_RETRIES = 4;
const DEFAULT_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const getRetryDelayMs = (response: Response, attempt: number): number => {
  const header = response.headers.get('Retry-After');
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds)) return seconds * 1000;
    const dateMs = Date.parse(header);
    if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  }
  return Math.min(DEFAULT_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
};

const fetchGraphWithRetry = async (
  url: string,
  accessToken: string,
  isCancelled: () => boolean,
): Promise<Response> => {
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Prefer: 'outlook.timezone="UTC"',
      },
    });

    if (!RETRYABLE_STATUSES.has(response.status) || attempt >= MAX_RETRIES) {
      return response;
    }

    const delay = getRetryDelayMs(response, attempt);
    await sleep(delay);
    if (isCancelled()) return response;
  }
};

const SELECT_FIELDS = [
  'id',
  'iCalUId',
  'subject',
  'start',
  'end',
  'organizer',
  'attendees',
  'isOnlineMeeting',
  'onlineMeeting',
  'type',
  'seriesMasterId',
  'isCancelled',
].join(',');

interface UseOutlookCalendarOptions {
  /** UTC ISO start of the window. */
  startDateTime: string;
  /** UTC ISO end of the window. */
  endDateTime: string;
  /** Only fetch when true (e.g. while the modal is open). */
  enabled: boolean;
}

interface UseOutlookCalendarResult {
  meetings: OutlookMeeting[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the signed-in user's Outlook calendar for a date window, straight from
 * Microsoft Graph — nothing is persisted to our DB. Handles token acquisition
 * (silent, with popup fallback) and follows `@odata.nextLink` so the full window
 * is returned rather than just the first page.
 */
export const useOutlookCalendar = ({
  startDateTime,
  endDateTime,
  enabled,
}: UseOutlookCalendarOptions): UseOutlookCalendarResult => {
  const { instance, accounts } = useMsal();
  const [meetings, setMeetings] = useState<OutlookMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bumped by refetch() to force the effect to re-run.
  const [reloadToken, setReloadToken] = useState(0);
  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  // Tracks the latest request so stale responses (range changed mid-flight)
  // don't overwrite fresh state.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const account = accounts[0];
    if (!account) {
      setError('No authenticated account found');
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const acquireToken = async (): Promise<string> => {
      const tokenRequest = { scopes: MS_SCOPES, account };
      try {
        const res = await instance.acquireTokenSilent(tokenRequest);
        return res.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          const res = await instance.acquireTokenPopup(tokenRequest);
          return res.accessToken;
        }
        throw err;
      }
    };

    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const accessToken = await acquireToken();

        const params = new URLSearchParams({
          startDateTime,
          endDateTime,
          $select: SELECT_FIELDS,
          $orderby: 'start/dateTime',
          $top: String(PAGE_SIZE),
        });

        let url: string | undefined = `${CALENDAR_VIEW_ENDPOINT}?${params.toString()}`;
        const collected: OutlookMeeting[] = [];
        let page = 0;

        while (url && page < MAX_PAGES) {
          const response: Response = await fetchGraphWithRetry(
            url,
            accessToken,
            () => cancelled || requestId !== requestIdRef.current,
          );

          const data: GraphCalendarViewResponse & {
            error?: { message?: string };
          } = await response.json();

          if (!response.ok) {
            throw new Error(data?.error?.message || `Graph request failed (${response.status})`);
          }

          for (const event of data.value ?? []) {
            collected.push(mapGraphEventToOutlookMeeting(event));
          }

          url = data['@odata.nextLink'];
          page += 1;
        }

        if (!cancelled && requestId === requestIdRef.current) {
          setMeetings(collected);
        }
      } catch (err) {
        if (!cancelled && requestId === requestIdRef.current) {
          console.error('[Outlook] Error fetching calendar:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch Outlook meetings');
          setMeetings([]);
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled, startDateTime, endDateTime, reloadToken, instance, accounts]);

  return { meetings, isLoading, error, refetch };
};
